
import { Stack, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ErrorModal, PageContainer, RootContainer } from "./SSORedirectError.styles";
import { ButtonText, Header } from "../../components";
import { AUTH_MODE } from "../../consts";
import { useGetLanguages, useGetTranslations } from "../../queries";
import { useAuthStore, buildSSOUrlWithFromUrl, useLanguageStore } from "../../stores";
import { getEnvironmentVariables } from "../../utils/general";
import { getLanguage } from "../../utils/language";

export function SSORedirectErrorPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setSpinnerLoginVisible = useAuthStore(state => state.setSpinnerLoginVisible);
  const resetError = useAuthStore(state => state.resetError);
  const theme = useTheme();
  const ssoEndpoint = useAuthStore(state => state.ssoEndpoint);
  const authSSOIsAllowed = useAuthStore(state =>
    state.allowedAuthentications.includes(AUTH_MODE.SSO)
  );
  const languageCookie = useLanguageStore(state => state.languageCookie);
  const setLanguage = useLanguageStore(state => state.setLanguage);
  setSpinnerLoginVisible(false);

  const { data: multiLanguageData } = useGetLanguages({ showMainLayout: false });
  const languages = multiLanguageData?.languages;
  const isMultilanguage = multiLanguageData?.enable_multilanguage;

  const storedLanguageId = getLanguage()?.id;
  const languageFromCookie = languages?.find(lang => lang.id === storedLanguageId);
  const languageFromStore = languages?.find(lang => lang.id === languageCookie?.id);
  const pageLanguage = languageFromCookie ?? languageFromStore ?? multiLanguageData?.primary_language;
  useGetTranslations({ language: pageLanguage, setLanguage });

  const goToManualLogin = () => {
    resetError();
    // rimuove il flag anti-loop SSO: l'utente ha scelto esplicitamente il login manuale
    sessionStorage.removeItem("disable_sso_redirect");
    navigate("/access/login");
  };

  const autoRedirectSsoLogin = ()=>{
    if (!ssoEndpoint) {
      goToManualLogin();
      return;
    }

    const ssoUrl = buildSSOUrlWithFromUrl(ssoEndpoint);
    if (getLoginType() === "sso" && authSSOIsAllowed) {
      window.location.replace(ssoUrl);
    }else{
      goToManualLogin();
    }
  };

  const getLoginType = () => {
    const { loginMode } = getEnvironmentVariables();
    const urlParams = new URLSearchParams(window.location.search);
    let loginType = loginMode?.toLowerCase();
    const urlLoginType = urlParams.get("loginType");
    loginType = (urlLoginType === "sso" || urlLoginType === "manual") ? urlLoginType : (loginType || "");

    const identityProvider = urlParams.get("identity_provider");
    // se presente identity_provider allora loginType è sso
    if (identityProvider) {
      loginType = "sso";
    }

    return loginType;
  };

  return (
    <PageContainer>
      <Header
        authenticated={ false }
        isMultilanguage={ isMultilanguage }
        languages={ languages }
        isPreLogin={ false }
      />
      <RootContainer>
        <ErrorModal theme={ theme }>
          <Stack
            alignItems="center"
            gap={ 4 }
            sx={ { "&:focus-visible": {
              outline: "none"
            } } }
          >
            <Stack
              alignItems="center"
              gap={ 1 }
            >
              <Typography
                aria-labelledby="modal-modal-title"
                color={ theme.customColors.textWidget }
                fontSize="2.2rem"
                fontWeight={ 900 }
              >
                { t("error_message") }
              </Typography>
              <Typography
                aria-describedby="modal-modal-description"
                color={ theme.customColors.textWidget }
                fontSize="1.15rem"
              >
                { t("session_critical_error") }
              </Typography>
            </Stack>
            <ButtonText
              onClick={ autoRedirectSsoLogin }
              aria-labelledby="modal-modal-continue"
              sx={ {
                ":hover": { backgroundColor: alpha(theme.customColors.backgroundPrimaryCta, 0.9) }
              } }
            >
              { t("retry") }
            </ButtonText>
          </Stack>
        </ErrorModal>
      </RootContainer>
    </PageContainer>
  );
}
